import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Search, HelpCircle, X, ChevronDown } from "lucide-react";
import { Reveal } from "../components/common";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { publicApi } from "../lib/api";
import useAskCassieStatus from "../hooks/useAskCassieStatus";
import Seo, { faqSchema, breadcrumbSchema } from "../components/Seo";

const CATEGORY_ORDER = [
  "About CASS", "Pricing & Getting Started", "Working With Us", "Bookkeeping", "Payroll", "VAT",
  "Self Assessment & Personal Tax", "Limited Company & Corporation Tax", "Business Growth & Planning",
  "Xero & Cloud Software", "Making Tax Digital", "Switching Accountants", "Starting a Business", "General",
];

const catSlug = (s) => (s || "General").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [query, setQuery] = useState("");
  const [openCats, setOpenCats] = useState({});
  // Once the answer library has taken this page's place, /faqs sends people
  // there rather than keeping a second version of the same answers.
  const { faqs_retired: retired } = useAskCassieStatus();

  useEffect(() => { publicApi.faqs().then(setFaqs).catch(() => setFaqs([])); }, []);

  const q = query.trim().toLowerCase();
  const terms = q.split(/\s+/).filter(Boolean);
  const filtered = useMemo(
    () => (terms.length ? faqs.filter((f) => {
      const hay = `${f.question} ${f.answer} ${f.category}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    }) : faqs),
    [faqs, q] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((f) => {
      const c = f.category || "General";
      (map[c] = map[c] || []).push(f);
    });
    return Object.keys(map)
      .sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a);
        const ib = CATEGORY_ORDER.indexOf(b);
        return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
      })
      .map((c) => ({ category: c, items: map[c] }));
  }, [filtered]);

  const scrollToCat = (c) => {
    setOpenCats((prev) => ({ ...prev, [c]: true }));
    setTimeout(() => document.getElementById(`faq-cat-${catSlug(c)}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const toggleCat = (c) => setOpenCats((prev) => ({ ...prev, [c]: !prev[c] }));

  if (retired) return <Navigate to="/ask-cassie" replace />;

  return (
    <div data-testid="faqs-page">
      <Seo
        title="Accounting FAQs"
        description="Answers to common questions about bookkeeping, payroll, VAT, tax, Xero, outsourced finance, cash flow forecasting and switching accountants — from CASS, your Xero Platinum Partner accountants in Tamworth serving the wider Midlands."
        jsonLd={
          faqs.length
            ? [
                faqSchema(faqs),
                breadcrumbSchema([
                  { name: "Home", path: "/" },
                  { name: "FAQs", path: "/faqs" },
                ]),
              ]
            : undefined
        }
      />

      {/* Hero */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase"><HelpCircle size={16} /> Help Centre</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">Frequently Asked<br />Questions</h1>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Clear, jargon-free answers about bookkeeping, payroll, VAT, tax, Xero and working with CASS. Can't find what you're looking for? We're only a conversation away.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="faqs-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions… e.g. VAT, Xero, tax return"
              className="w-full rounded-full bg-white/95 focus:bg-white text-navy pl-12 pr-11 py-4 text-sm font-medium outline-none focus:ring-2 focus:ring-orange shadow-lg"
            />
            {query && (
              <button onClick={() => setQuery("")} aria-label="Clear search" data-testid="faqs-search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy">
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category quick-nav (hidden while searching) */}
      {!q && grouped.length > 1 && (
        <section className="bg-slate-50 border-b border-slate-100 sticky top-0 z-20">
          <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap gap-2 justify-center" data-testid="faqs-category-nav">
            {grouped.map(({ category }) => (
              <button
                key={category}
                onClick={() => scrollToCat(category)}
                className="text-xs font-semibold px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-navy hover:border-orange hover:text-orange transition-colors"
              >
                {category}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* FAQ content */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {q && (
            <p className="text-sm text-slate-500 mb-8" data-testid="faqs-result-count">
              {filtered.length} result{filtered.length === 1 ? "" : "s"} for “{query.trim()}”
            </p>
          )}

          {grouped.length === 0 ? (
            <div className="text-center py-16" data-testid="faqs-empty">
              <HelpCircle size={40} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-6">We couldn't find an answer matching that. Let's talk it through instead.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">Ask us directly <ArrowRight size={16} /></Link>
            </div>
          ) : (
            <div className="space-y-4">
              {grouped.map(({ category, items }) => {
                const open = !!q || !!openCats[category];
                return (
                  <div key={category} id={`faq-cat-${catSlug(category)}`} className="scroll-mt-24 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`faq-section-${catSlug(category)}`}>
                    <button
                      type="button"
                      onClick={() => toggleCat(category)}
                      data-testid={`faq-cat-toggle-${catSlug(category)}`}
                      className="w-full flex items-center gap-3 px-5 md:px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="h-7 w-1.5 rounded-full bg-orange shrink-0" />
                      <h2 className="font-display font-extrabold text-lg md:text-xl text-navy flex-1">{category}</h2>
                      <span className="text-xs font-semibold text-slate-400">{items.length}</span>
                      <ChevronDown size={20} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {/* Rendered always (visually hidden when collapsed) so the FAQ
                        answers are present in the DOM for crawlers — SEO fix A1-2. */}
                    <div hidden={!open} className="px-3 md:px-4 pb-4 pt-1">
                        <Accordion type="single" collapsible className="space-y-3">
                          {items.map((f, i) => (
                            <AccordionItem key={`${category}-${i}`} value={`${category}-${i}`} className="bg-slate-50 rounded-xl border border-slate-100 px-5">
                              <AccordionTrigger className="text-left font-display font-bold text-navy hover:no-underline">{f.question}</AccordionTrigger>
                              <AccordionContent className="text-slate-600 leading-relaxed">
                                <p className="whitespace-pre-line">{f.answer}</p>
                                {f.link_url && (
                                  f.link_url.startsWith("/") ? (
                                    <Link to={f.link_url} data-testid={`faq-link-${catSlug(category)}-${i}`} className="mt-4 inline-flex items-center gap-2 bg-navy hover:bg-orange text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                                      {f.link_label || "Learn more"} <ArrowRight size={15} />
                                    </Link>
                                  ) : (
                                    <a href={f.link_url} target="_blank" rel="noopener noreferrer" data-testid={`faq-link-${catSlug(category)}-${i}`} className="mt-4 inline-flex items-center gap-2 bg-navy hover:bg-orange text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                                      {f.link_label || "Learn more"} <ArrowRight size={15} />
                                    </a>
                                  )
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-3xl mx-auto px-6 py-16 md:py-20 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl uppercase leading-tight">Still have a question?</h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-xl mx-auto">Every business is different. Tell us a little about yours and we'll give you a straight, jargon-free answer — no pressure, no obligation.</p>
            <div className="mt-8">
              <Link to="/contact" data-testid="faqs-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Let's Talk <ArrowRight size={18} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
